import { apiClient } from './client';

// ============================================================================
// TYPES
// ============================================================================

export type ServiceCategory =
  | 'TUITION'
  | 'TRANSPORT'
  | 'MEALS'
  | 'BOARDING'
  | 'UNIFORM'
  | 'BOOKS'
  | 'ACTIVITIES'
  | 'EXAM_FEES'
  | 'LAB_FEES'
  | 'LIBRARY'
  | 'TECHNOLOGY'
  | 'INSURANCE'
  | 'OTHER';

export type BillingFrequency = 'ONE_TIME' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'TERMLY' | 'YEARLY';
export type ServiceApplicability = 'ALL' | 'SPECIFIC_GRADES' | 'SPECIFIC_STREAMS';
export type PricingType = 'FIXED' | 'VARIABLE';
export type ScheduleFrequency = 'ONCE' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'TERMLY' | 'YEARLY';
export type TargetType = 'ALL_STUDENTS' | 'GRADE' | 'STREAM' | 'CUSTOM';
export type BatchJobStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'PARTIAL';
export type ReminderType = 'BEFORE_DUE' | 'ON_DUE' | 'OVERDUE' | 'ESCALATION';
export type ReminderChannel = 'EMAIL' | 'SMS' | 'PUSH' | 'IN_APP';

// Billable Service
export interface GradePricing {
  gradeId: string;
  gradeName?: string;
  amount: number;
}

export interface BillableService {
  _id: string;
  institutionId: string;
  code?: string;
  name: string;
  description?: string;
  category: ServiceCategory;
  billingFrequency: BillingFrequency;
  baseAmount: number;
  basePrice?: number; // Alias for baseAmount
  pricingType: PricingType; // FIXED = cannot change price, VARIABLE = can adjust price during invoicing
  minPrice?: number; // For VARIABLE pricing - minimum allowed price
  maxPrice?: number; // For VARIABLE pricing - maximum allowed price
  gradePricing: GradePricing[];
  applicability: ServiceApplicability;
  applicableGrades?: string[];
  applicableStreams?: string[];
  taxRate: number;
  discountable: boolean;
  refundable: boolean;
  optional: boolean;
  isActive: boolean;
  createdBy: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  createdAt: string;
  updatedAt: string;
}

// Service Enrollment
export interface ServiceEnrollment {
  _id: string;
  institutionId: string;
  serviceId: string | BillableService;
  studentId: string;
  studentName?: string;
  gradeId?: string;
  streamId?: string;
  enrolledAt: string;
  enrolledBy: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  effectiveFrom: string;
  effectiveTo?: string;
  status: 'ACTIVE' | 'PAUSED' | 'CANCELLED';
  customAmount?: number;
  discount?: number;
  discountType?: 'FIXED' | 'PERCENTAGE';
  discountReason?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// Invoice Schedule
export interface InvoiceSchedule {
  _id: string;
  institutionId: string;
  name: string;
  description?: string;
  serviceIds: string[] | BillableService[];
  frequency: ScheduleFrequency;
  targetType: TargetType;
  targetGrades?: string[];
  targetStreams?: string[];
  targetStudents?: string[];
  dayOfMonth?: number;
  dayOfWeek?: number;
  scheduledTime?: string;
  nextRunDate: string;
  lastRunDate?: string;
  dueOffsetDays: number;
  autoSend: boolean;
  notifyParents: boolean;
  isActive: boolean;
  runCount: number;
  lastRunStatus?: 'SUCCESS' | 'PARTIAL' | 'FAILED';
  lastRunInvoiceCount?: number;
  createdBy: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  createdAt: string;
  updatedAt: string;
}

// Reminder Config
export interface ReminderConfig {
  _id: string;
  institutionId: string;
  name: string;
  reminderType: ReminderType;
  channels: ReminderChannel[];
  daysOffset: number;
  frequency: 'ONCE' | 'DAILY' | 'WEEKLY';
  maxReminders: number;
  emailTemplate?: string;
  smsTemplate?: string;
  targetInvoiceTypes?: string[];
  minAmount?: number;
  isActive: boolean;
  createdBy: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  createdAt: string;
  updatedAt: string;
}

// Reminder Log
export interface ReminderLog {
  _id: string;
  institutionId: string;
  configId: string | ReminderConfig;
  invoiceId: string;
  invoiceNumber?: string;
  recipientName: string;
  recipientEmail?: string;
  recipientPhone?: string;
  channel: ReminderChannel;
  status: 'PENDING' | 'SENT' | 'DELIVERED' | 'FAILED' | 'BOUNCED';
  error?: string;
  sentAt?: string;
  deliveredAt?: string;
  createdAt: string;
}

// Batch Invoice Job
export interface BatchInvoiceJob {
  _id: string;
  institutionId: string;
  name: string;
  targetType: TargetType;
  targetGrades?: string[];
  targetStreams?: string[];
  targetStudents?: string[];
  serviceIds: string[];
  totalStudents: number;
  processedCount: number;
  successCount: number;
  failedCount: number;
  status: BatchJobStatus;
  errors?: Array<{
    studentId: string;
    studentName: string;
    error: string;
  }>;
  createdInvoices: string[];
  dueDate: string;
  autoSend: boolean;
  startedAt?: string;
  completedAt?: string;
  createdBy: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  createdAt: string;
  updatedAt: string;
}

// Billing Stats
export interface BillingStats {
  services: {
    total: number;
    active: number;
    byCategory: Array<{ _id: ServiceCategory; count: number }>;
  };
  enrollments: {
    total: number;
    active: number;
    byService: Array<{ _id: string; serviceName: string; count: number }>;
  };
  schedules: {
    total: number;
    active: number;
    upcoming: Array<{
      _id: string;
      name: string;
      nextRunDate: string;
      frequency: ScheduleFrequency;
    }>;
  };
  batchJobs: {
    total: number;
    completed: number;
    totalInvoicesGenerated: number;
    recentJobs: Array<{
      _id: string;
      name: string;
      status: BatchJobStatus;
      successCount: number;
      createdAt: string;
    }>;
  };
  reminders: {
    configsActive: number;
    sentLast30Days: number;
    deliveryRate: number;
  };
}

// Preview Result
export interface PreviewResult {
  totalStudents: number;
  totalAmount: number;
  students: Array<{
    studentId: string;
    studentName: string;
    grade: string;
    stream?: string;
    services: Array<{
      serviceId: string;
      serviceName: string;
      amount: number;
    }>;
    totalAmount: number;
  }>;
  services: Array<{
    serviceId: string;
    serviceName: string;
    studentCount: number;
    totalAmount: number;
  }>;
}

// ============================================================================
// REQUEST TYPES
// ============================================================================

export interface CreateServiceData {
  name: string;
  description?: string;
  category: ServiceCategory;
  billingFrequency: BillingFrequency;
  baseAmount: number;
  basePrice?: number; // Backend field name - will be set from baseAmount if not provided
  pricingType?: PricingType;
  minPrice?: number;
  maxPrice?: number;
  gradePricing?: GradePricing[];
  applicability: ServiceApplicability;
  applicableGrades?: string[];
  applicableStreams?: string[];
  taxRate?: number;
  discountable?: boolean;
  refundable?: boolean;
  optional?: boolean;
}

export interface EnrollStudentsData {
  serviceId: string;
  studentIds: string[];
  effectiveFrom?: string;
  effectiveTo?: string;
  customAmount?: number;
  discount?: number;
  discountType?: 'FIXED' | 'PERCENTAGE';
  discountReason?: string;
  notes?: string;
}

export interface CreateScheduleData {
  name: string;
  description?: string;
  serviceIds: string[];
  frequency: ScheduleFrequency;
  targetType: TargetType;
  targetGrades?: string[];
  targetStreams?: string[];
  targetStudents?: string[];
  dayOfMonth?: number;
  dayOfWeek?: number;
  scheduledTime?: string;
  nextRunDate: string;
  dueOffsetDays?: number;
  autoSend?: boolean;
  notifyParents?: boolean;
}

export interface CreateReminderConfigData {
  name: string;
  reminderType: ReminderType;
  channels: ReminderChannel[];
  daysOffset: number;
  frequency?: 'ONCE' | 'DAILY' | 'WEEKLY';
  maxReminders?: number;
  emailTemplate?: string;
  smsTemplate?: string;
  targetInvoiceTypes?: string[];
  minAmount?: number;
}

export interface GenerateBatchInvoicesData {
  name: string;
  targetType: TargetType;
  targetGrades?: string[];
  targetStreams?: string[];
  targetStudents?: string[];
  serviceIds: string[];
  dueDate: string;
  autoSend?: boolean;
}

export interface PreviewBatchData {
  targetType: TargetType;
  targetGrades?: string[];
  targetStreams?: string[];
  targetStudents?: string[];
  serviceIds: string[];
}

// ============================================================================
// SERVICES API
// ============================================================================

export async function getServices(params?: {
  category?: ServiceCategory;
  isActive?: boolean;
  search?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    });
  }
  const response = await apiClient.get(`/billing/services?${searchParams.toString()}`);
  return response.data;
}

export async function getServiceById(id: string) {
  const response = await apiClient.get(`/billing/services/${id}`);
  return response.data;
}

export async function createService(data: CreateServiceData) {
  // Ensure basePrice is set (backend field name) from baseAmount
  const payload = {
    ...data,
    basePrice: data.basePrice ?? data.baseAmount,
  };
  const response = await apiClient.post('/billing/services', payload);
  return response.data;
}

export async function updateService(id: string, data: Partial<CreateServiceData> & { isActive?: boolean }) {
  const response = await apiClient.put(`/billing/services/${id}`, data);
  return response.data;
}

export async function deleteService(id: string) {
  const response = await apiClient.delete(`/billing/services/${id}`);
  return response.data;
}

// ============================================================================
// ENROLLMENTS API
// ============================================================================

export async function getEnrollments(params?: {
  serviceId?: string;
  studentId?: string;
  status?: 'ACTIVE' | 'PAUSED' | 'CANCELLED';
  gradeId?: string;
  page?: number;
  limit?: number;
}) {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    });
  }
  const response = await apiClient.get(`/billing/enrollments?${searchParams.toString()}`);
  return response.data;
}

export async function enrollStudents(data: EnrollStudentsData) {
  const response = await apiClient.post('/billing/enrollments', data);
  return response.data;
}

export async function unenrollStudent(serviceId: string, studentId: string) {
  const response = await apiClient.delete(`/billing/enrollments/${serviceId}/${studentId}`);
  return response.data;
}

// ============================================================================
// SCHEDULES API
// ============================================================================

export async function getSchedules(params?: {
  isActive?: boolean;
  frequency?: ScheduleFrequency;
}) {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    });
  }
  const response = await apiClient.get(`/billing/schedules?${searchParams.toString()}`);
  return response.data;
}

export async function createSchedule(data: CreateScheduleData) {
  const response = await apiClient.post('/billing/schedules', data);
  return response.data;
}

export async function updateSchedule(id: string, data: Partial<CreateScheduleData>) {
  const response = await apiClient.put(`/billing/schedules/${id}`, data);
  return response.data;
}

export async function toggleScheduleStatus(id: string) {
  const response = await apiClient.patch(`/billing/schedules/${id}/status`);
  return response.data;
}

export async function deleteSchedule(id: string) {
  const response = await apiClient.delete(`/billing/schedules/${id}`);
  return response.data;
}

// ============================================================================
// BATCH INVOICE API
// ============================================================================

export async function previewBatchInvoices(data: PreviewBatchData) {
  const response = await apiClient.post('/billing/batch/preview', data);
  return response.data;
}

export async function generateBatchInvoices(data: GenerateBatchInvoicesData) {
  const response = await apiClient.post('/billing/batch/generate', data);
  return response.data;
}

export async function getBatchJobs(params?: {
  status?: BatchJobStatus;
  page?: number;
  limit?: number;
}) {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    });
  }
  const response = await apiClient.get(`/billing/batch/jobs?${searchParams.toString()}`);
  return response.data;
}

// ============================================================================
// REMINDERS API
// ============================================================================

export async function getReminderConfigs(params?: { isActive?: boolean }) {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    });
  }
  const response = await apiClient.get(`/billing/reminders/configs?${searchParams.toString()}`);
  return response.data;
}

export async function createReminderConfig(data: CreateReminderConfigData) {
  const response = await apiClient.post('/billing/reminders/configs', data);
  return response.data;
}

export async function updateReminderConfig(id: string, data: Partial<CreateReminderConfigData> & { isActive?: boolean }) {
  const response = await apiClient.put(`/billing/reminders/configs/${id}`, data);
  return response.data;
}

export async function deleteReminderConfig(id: string) {
  const response = await apiClient.delete(`/billing/reminders/configs/${id}`);
  return response.data;
}

export async function getReminderLogs(params?: {
  configId?: string;
  invoiceId?: string;
  status?: 'PENDING' | 'SENT' | 'DELIVERED' | 'FAILED' | 'BOUNCED';
  channel?: ReminderChannel;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}) {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    });
  }
  const response = await apiClient.get(`/billing/reminders/logs?${searchParams.toString()}`);
  return response.data;
}

// ============================================================================
// STATS API
// ============================================================================

export async function getBillingStats() {
  const response = await apiClient.get('/billing/stats');
  return response.data;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export const SERVICE_CATEGORIES: Record<ServiceCategory, { label: string; color: string }> = {
  TUITION: { label: 'Tuition', color: 'blue' },
  TRANSPORT: { label: 'Transport', color: 'green' },
  MEALS: { label: 'Meals', color: 'orange' },
  BOARDING: { label: 'Boarding', color: 'purple' },
  UNIFORM: { label: 'Uniform', color: 'pink' },
  BOOKS: { label: 'Books', color: 'cyan' },
  ACTIVITIES: { label: 'Activities', color: 'yellow' },
  EXAM_FEES: { label: 'Exam Fees', color: 'red' },
  LAB_FEES: { label: 'Lab Fees', color: 'indigo' },
  LIBRARY: { label: 'Library', color: 'teal' },
  TECHNOLOGY: { label: 'Technology', color: 'violet' },
  INSURANCE: { label: 'Insurance', color: 'slate' },
  OTHER: { label: 'Other', color: 'gray' },
};

export const BILLING_FREQUENCIES: Record<BillingFrequency, string> = {
  ONE_TIME: 'One Time',
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  TERMLY: 'Per Term',
  YEARLY: 'Yearly',
};

export const SCHEDULE_FREQUENCIES: Record<ScheduleFrequency, string> = {
  ONCE: 'Once',
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  TERMLY: 'Per Term',
  YEARLY: 'Yearly',
};

export const TARGET_TYPES: Record<TargetType, string> = {
  ALL_STUDENTS: 'All Students',
  GRADE: 'By Grade',
  STREAM: 'By Stream',
  CUSTOM: 'Custom Selection',
};

export const REMINDER_TYPES: Record<ReminderType, { label: string; description: string }> = {
  BEFORE_DUE: { label: 'Before Due', description: 'Send reminders before the due date' },
  ON_DUE: { label: 'On Due Date', description: 'Send reminders on the due date' },
  OVERDUE: { label: 'Overdue', description: 'Send reminders after the due date' },
  ESCALATION: { label: 'Escalation', description: 'Escalate to school administration' },
};

export const REMINDER_CHANNELS: Record<ReminderChannel, { label: string; icon: string }> = {
  EMAIL: { label: 'Email', icon: 'EnvelopeIcon' },
  SMS: { label: 'SMS', icon: 'DevicePhoneMobileIcon' },
  PUSH: { label: 'Push Notification', icon: 'BellIcon' },
  IN_APP: { label: 'In-App', icon: 'InboxIcon' },
};

export function getCategoryColor(category: ServiceCategory): string {
  return SERVICE_CATEGORIES[category]?.color || 'gray';
}

export function getCategoryLabel(category: ServiceCategory): string {
  return SERVICE_CATEGORIES[category]?.label || category;
}

export function getFrequencyLabel(frequency: BillingFrequency | ScheduleFrequency): string {
  return BILLING_FREQUENCIES[frequency as BillingFrequency] || SCHEDULE_FREQUENCIES[frequency as ScheduleFrequency] || frequency;
}

export function getTargetTypeLabel(targetType: TargetType): string {
  return TARGET_TYPES[targetType] || targetType;
}

export function getBatchStatusColor(status: BatchJobStatus): string {
  const colors: Record<BatchJobStatus, string> = {
    PENDING: 'yellow',
    PROCESSING: 'blue',
    COMPLETED: 'green',
    FAILED: 'red',
    PARTIAL: 'orange',
  };
  return colors[status] || 'gray';
}

export function formatCurrency(amount: number, currency: string = 'KES'): string {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency,
  }).format(amount);
}
